import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

export interface InboxThread {
  id: string;
  type: string;
  property_id: string | null;
  task_id: string | null;
  company_id: string | null;
  last_message_at: string | null;
  participants: string[];
  created_at: string;
  // Enriched data
  property_name?: string;
  task_date?: string;
  last_message?: string;
  /** Who sent the last message (for "X messaged about Task • date") */
  last_message_sender_name?: string | null;
  unread_count?: number;
}

export const useInbox = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["inbox-threads"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      // Fetch all threads where user is a participant
      const { data: threads, error } = await supabase
        .from("message_threads")
        .select("*")
        .order("last_message_at", { ascending: false, nullsFirst: false });

      if (error && (error.code === '400' || error.code === '404')) return [];
      if (error) throw error;
      if (!threads) return [];

      // Enrich threads with property names, task dates, and last messages
      const enrichedThreads: InboxThread[] = await Promise.all(
        threads.map(async (thread) => {
          const enriched: InboxThread = { ...thread };

          // Get property name
          if (thread.property_id) {
            const { data: property } = await supabase
              .from("properties")
              .select("name")
              .eq("id", thread.property_id)
              .maybeSingle();
            enriched.property_name = property?.name;
          }

          // Get task date
          if (thread.task_id) {
            const { data: task, error: taskError } = await supabase
              .from("cleaning_tasks")
              .select("*")
              .eq("id", thread.task_id)
              .maybeSingle();
            
            // TEMPORARY: Handle errors gracefully
            if (taskError) {
              console.error("[useInbox] Error fetching task:", taskError);
            } else {
              enriched.task_date = task?.scheduled_date;
            }
          }

          // Get last message and sender for "who messaged"
          const { data: lastMessage } = await supabase
            .from("messages")
            .select("message_text, created_at, sender_user_id")
            .eq("thread_id", thread.id)
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();

          if (lastMessage) {
            enriched.last_message = lastMessage.message_text;
            const senderId = (lastMessage as { sender_user_id?: string }).sender_user_id;
            if (senderId) {
              const [{ data: profile }, { data: cleaner }] = await Promise.all([
                supabase.from("profiles").select("full_name").eq("id", senderId).maybeSingle(),
                supabase.from("cleaners").select("name").eq("user_id", senderId).maybeSingle(),
              ]);
              const name = (profile as { full_name?: string } | null)?.full_name?.trim()
                || (cleaner as { name?: string } | null)?.name?.trim()
                || null;
              enriched.last_message_sender_name = name || undefined;
            }
          }

          // Unread = messages from others (not sent by current user)
          const { count, error: countError } = await supabase
            .from("messages")
            .select("*", { count: "exact", head: true })
            .eq("thread_id", thread.id)
            .neq("sender_user_id", user.id);

          // Handle 400/404 as empty state
          if (countError && (countError.code === '400' || countError.code === '404')) {
            enriched.unread_count = 0;
          } else if (countError) {
            // Only log non-expected errors
            console.error("[useInbox] Error counting messages:", {
              message: countError.message,
              code: countError.code,
            });
            enriched.unread_count = 0;
          } else {
            enriched.unread_count = count || 0;
          }

          return enriched;
        })
      );

      return enrichedThreads;
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Set up real-time subscription for new messages
  useEffect(() => {
    const channel = supabase
      .channel("inbox-updates")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "messages",
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["inbox-threads"] });
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "message_threads",
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["inbox-threads"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return query;
};
